import {
  NutritionEntryDto,
  NutritionTargetDto,
  RecomendedTargetBriefInfo,
} from "@growth-machine-llc/stridist-api-client";
import dayjs, { Dayjs } from "dayjs";
import { groupBy, chunk } from "lodash";

const GARMIN_PROVIDER = "garmin";

export const interpolateDataContinuous = (
  data: any[],
  noPadding?: boolean,
  averagingWindow: "day" | "week" | "month" | "year" = "day",
) => {
  const roundValue = (value: number) =>
    Math.round(value * 10 + Number.EPSILON) / 10;

  const result: {
    x: string;
    y?: number;
    year: string;
    interpolated?: boolean;
  }[] = [];
  const start = dayjs(data[0]?.x).startOf("month");
  const end = dayjs(data[data.length - 1]?.x).endOf("month");

  const timePeriod = averagingWindow === "week" ? "isoWeek" : averagingWindow;

  for (
    let d = start;
    d.isBefore(end, "day") || d.isSame(end, "day");
    d = d.add(1, "day")
  ) {
    const week = data.filter((week) => week.x.isSame(d, timePeriod));

    const isDataStart =
      (!noPadding && d.isSame(start, "day")) ||
      (noPadding && d.isSame(data[0]?.x, "day"));

    if (week.length) {
      result.push({
        x: d.startOf("day").format("YYYY-MM-DD"),
        y: roundValue(
          week.reduce((acc, curr) => acc + curr.y, 0) / week.length,
        ),
        year:
          d.isSame(d.startOf("year"), "day") || isDataStart
            ? d.format("YYYY")
            : undefined,
        interpolated: !week.find((day) => day.x.isSame(d, "day")),
      });
    } else {
      const last_el = result[result.length - 1];
      const prev = last_el && last_el.y ? last_el : null;
      const next = data.find((day) => day.x.isAfter(d, "day"));

      if (prev && next) {
        const trueFactor =
          d.diff(prev.x, averagingWindow) /
          (next.x.diff(prev.x, averagingWindow) || 1);
        const factor = Math.min(1, Math.max(0, trueFactor));
        const interpolatedValue = prev.y + factor * (next.y - prev.y);

        result.push({
          x: d.startOf("day").format("YYYY-MM-DD"),
          y: roundValue(interpolatedValue),
          year:
            d.isSame(d.startOf("year"), "day") || isDataStart
              ? d.format("YYYY")
              : undefined,
          interpolated: true,
        });
      } else if (!noPadding) {
        result.push({
          x: d.startOf("day").format("YYYY-MM-DD"),
          year:
            d.isSame(d.startOf("year"), "day") || isDataStart
              ? d.format("YYYY")
              : undefined,
          interpolated: true,
        });
      }
    }
  }

  return result;
};

export const interpolateDataDiscrete = (
  data: any[],
  noPadding = false,
  averagingWindow: "day" | "week" | "month" | "year" = "day",
) => {
  const roundValue = (value: number) =>
    Math.round(value * 10 + Number.EPSILON) / 10;

  const result: {
    x: string;
    y?: number;
    year: string;
    //used to identify if data point has garmin source
    includeGarminData?: boolean;
  }[] = [];
  const start = dayjs(data[0]?.x).startOf("year");
  const end = dayjs(data[data.length - 1]?.x).endOf("year");

  const timePeriod = averagingWindow === "week" ? "isoWeek" : averagingWindow;

  for (
    let d = start;
    d.isBefore(end, "day") || d.isSame(end, "day");
    d = d.add(1, averagingWindow)
  ) {
    const week = data.filter((week) => week.x.isSame(d, timePeriod));

    const hasGarmin = week.some(
      (item) => item.provider?.toLowerCase() === GARMIN_PROVIDER,
    );

    const isDataStart =
      (!noPadding && d.isSame(start, averagingWindow)) ||
      (noPadding && d.isSame(data[0]?.x, averagingWindow));

    if (week.length) {
      result.push({
        x: d.startOf(averagingWindow).format("YYYY-MM-DD"),
        y: roundValue(week.reduce((acc, curr) => acc + curr.y, 0)),
        year:
          d.isSame(d.startOf("year"), averagingWindow) || isDataStart
            ? d.format("YYYY")
            : undefined,
        includeGarminData: hasGarmin,
      });
    } else {
      const noPrev = result.length === 0;
      const noNext = !data.find((day) => day.x.isAfter(d));
      if ((!noPrev && !noNext) || ((noPrev || noNext) && !noPadding)) {
        result.push({
          x: d.startOf("day").format("YYYY-MM-DD"),
          y: undefined,
          year:
            d.isSame(d.startOf("year"), averagingWindow) || isDataStart
              ? d.format("YYYY")
              : undefined,
          includeGarminData: false,
        });
      }
    }
  }

  return result;
};

export interface InternalNutritionEntryDto {
  date: string;
  protein?: number;
  fat?: number;
  carbs?: number;
  calories?: number;
  targetCalories?: number;
  targetProtein?: number;
  targetCarbs?: number;
  targetFat?: number;
  targetId: number;
  year?: string;
  // used to identify if data point has garmin source
  includeGarminData?: boolean;
}

export const interpolateNutritionData = (
  inData: NutritionEntryDto[],
  targets: NutritionTargetDto[],
  targetRecomendations: RecomendedTargetBriefInfo[],
  noPadding = false,
  averagingWindow: "day" | "week" | "month" | "year" = "day",
  averageAll?: boolean,
  doAveraging?: boolean,
  hardDate?: Dayjs,
): InternalNutritionEntryDto[] => {
  const macroFields = ["protein", "fat", "carbs", "calories"];

  const roundValue = (value: number) => Math.round(value);
  const timeWindow = averagingWindow === "week" ? "isoWeek" : averagingWindow;

  // Helper to check if a record has any non-zero macro values
  const hasMacros = (entry: any) => {
    return Boolean(
      entry && (entry.calories || entry.protein || entry.fat || entry.carbs),
    );
  };

  // Ensure today is present in the data for chart completeness
  const today = dayjs();
  const hasToday = inData.some((entry) => entry.date.isSame(today, "day"));
  const data = hasToday
    ? [...inData]
    : [
        ...inData,
        {
          date: today,
          calories: 0,
          protein: 0,
          fat: 0,
          carbs: 0,
          provider: null,
        },
      ];
  data.sort((a, b) => a.date.diff(b.date, "days"));

  // Determine chart range
  const start = hardDate || dayjs(data[0]?.date as Dayjs).startOf("month");
  const end =
    hardDate || dayjs(data[data.length - 1]?.date as Dayjs).endOf("month");

  const sumOrAverageTargets = (
    targetsArr: NutritionTargetDto[],
    field: string,
  ) => {
    if (!targetsArr.length) return 0;
    const sum = targetsArr.reduce((acc, t) => acc + (t?.[field] || 0), 0);
    return roundValue(doAveraging ? sum / targetsArr.length : sum);
  };

  const getWeekTargets = (filteredRecs, week, isDay, targetsList) => {
    const ids =
      isDay || week.length === 0
        ? filteredRecs.map((rec) => rec.recomendedTargetId)
        : filteredRecs
            .filter((rec) =>
              week.some((day) => rec.date.isSame(day.date, "day")),
            )
            .map((rec) => rec.recomendedTargetId);
    return ids
      .map((id) => targetsList.find((target) => target.id === id))
      .filter((target) => hasMacros(target) || isDay);
  };

  const result: InternalNutritionEntryDto[] = [];

  for (
    let d = start;
    d.isBefore(end, "day") || d.isSame(end, "day");
    d = d.add(1, averagingWindow)
  ) {
    // Find entries for this period
    const week = averageAll
      ? data
      : data.filter(
          (entry) =>
            (entry.date as Dayjs).isSame(d, timeWindow) && hasMacros(entry),
        );

    // Find recommendations for this period
    const filteredRecs = targetRecomendations.filter(
      (rec) => rec.date.isSame(d, timeWindow) || averageAll,
    );

    // Get relevant targets
    const weekTargets = getWeekTargets(
      filteredRecs,
      week,
      averagingWindow === "day",
      targets,
    );

    // Helper to get year label
    const isDataStart =
      (!noPadding && d.isSame(start, timeWindow)) ||
      (noPadding && d.isSame(data[0]?.date as Dayjs, timeWindow));
    const yearLabel =
      d.isSame(d.startOf("year"), timeWindow) || isDataStart
        ? d.format("YYYY")
        : undefined;

    // Helper to get targetId
    const getTargetId = () => {
      const found = weekTargets.find(
        (target) => hasMacros(target) || averagingWindow === "day",
      );
      return found ? found.id : undefined;
    };

    // Helper to build entry
    const buildEntry = (
      protein,
      fat,
      carbs,
      calories,
      includeGarminData = false,
    ) => ({
      date: d.startOf(averagingWindow).format("YYYY-MM-DD"),
      protein,
      fat,
      carbs,
      calories,
      targetCalories: sumOrAverageTargets(weekTargets, "calories"),
      targetProtein: sumOrAverageTargets(weekTargets, "protein"),
      targetCarbs: sumOrAverageTargets(weekTargets, "carbs"),
      targetFat: sumOrAverageTargets(weekTargets, "fat"),
      targetId: getTargetId(),
      year: yearLabel,
      includeGarminData,
    });

    const includeGarminData = week.some(
      (entry) =>
        entry.provider?.toLowerCase() === GARMIN_PROVIDER.toLowerCase(),
    );

    if (week.length) {
      // Aggregate values for the period
      const sum = (field: string) =>
        week.reduce((acc, curr) => acc + (curr[field] || 0), 0);
      const avg = (field: string) =>
        week.length
          ? roundValue(doAveraging ? sum(field) / week.length : sum(field))
          : 0;
      result.push(
        buildEntry(
          ...(macroFields.map((field) => avg(field)) as [
            number,
            number,
            number,
            number,
          ]),
          includeGarminData,
        ),
      );
    } else {
      // Only add empty entry if not at the very start or end, or if noPadding is false
      const noPrev = result.length === 0;
      const noNext = !data.find((entry) => entry.date.isAfter(d));
      if ((!noPrev && !noNext) || ((noPrev || noNext) && !noPadding)) {
        result.push(buildEntry(0, 0, 0, 0));
      }
    }
    if (averageAll) break;
  }

  return result;
};

export const chunkBy = (
  data: any[],
  units: string[],
  dateField: string = "x",
  valueField: string = "y",
  filterEmpty = true,
): any[][] => {
  const weeks = groupBy(data, (item) =>
    units.map((unit) => dayjs(item[dateField])[unit]().toString()).join("-"),
  );
  return Object.values(weeks)
    .filter((week) => !week.every((day) => !day[valueField]) || !filterEmpty)
    .map((week) => {
      return week.sort((a, b) => dayjs(a[dateField]).diff(b[dateField]));
    })
    .sort((a, b) => dayjs(a[0][dateField]).diff(b[0][dateField]));
};

export const chunkKeepTail = (
  data: any[],
  count: number,
  filterEmpty: boolean = true,
  valueField: string = "y",
) => {
  const chunks = chunk(data.toReversed(), count).filter(
    (week) => !filterEmpty || !week.every((day) => !day[valueField]),
  );
  return chunks.map((chunk) => chunk.toReversed()).toReversed();
};
